/**
 * Workflow proxy admin handler
 * Manages N8N API proxy requests
 */

import { useState, useCallback } from "react";
import type { UseAdminAppReturn } from "../useAdminApp";

export interface WorkflowProxyAdminHandler {
  loading: boolean;
  error: string | null;
  result: any;
  executeRequest: (path: string, method?: string, body?: any) => Promise<any>;
  clearState: () => void;
}

export function useWorkflowProxyAdmin(adminApp: UseAdminAppReturn): WorkflowProxyAdminHandler {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  /**
   * Execute a proxied N8N API request
   */
  const executeRequest = useCallback(async (
    path: string,
    method: string = 'GET',
    body?: any
  ) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const url = `${adminApp.httpUrl}/api/workflow${path}`;
      const options: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      };

      if (body && method !== 'GET' && method !== 'HEAD') {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(url, options);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Request failed';
      setError(errorMessage);
      console.error('Workflow request error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  /**
   * Clear current state
   */
  const clearState = useCallback(() => {
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    loading,
    error,
    result,
    executeRequest,
    clearState,
  };
}

